/**
 * useUpdateCheckpoint Hook
 *
 * React Query mutation hook for updating checkpoint data.
 * Used by CheckpointEditModal for inline editing in PatrolDetailModal.
 *
 * Features:
 * - Wraps the existing SubmitChecklistV3 API
 * - Optimistic updates for immediate UI feedback
 * - Rollback on error
 * - Invalidates patrol detail query on success
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { updateCheckpoints } from '../../../../../api/canguard/updateCheckpoints';
import { PATROL_DETAIL_QUERY_KEY, TransformedCheckpoint } from './usePatrolDetail';
import { ACTIVITY_FEED_QUERY_KEY } from './useActivityFeed';
import { PatrolDetailsResponse } from '../../../../../api/activityReport/getPatrolDetails';

/**
 * Data structure for updating a single checkpoint
 * Matches the API payload format expected by SubmitChecklistV3
 */
export interface CheckpointUpdateData {
  checkpointId: number;
  comment?: string;
  selectedStatusId?: number | null;
  time?: string;
  dateTime?: string | null;
  selectedDropdownIds?: number[];
}

export interface UseUpdateCheckpointOptions {
  /** Schedule assignment ID for query invalidation */
  scheduleAssignmentId: number | null;
  /** Callback on successful update */
  onSuccess?: () => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

export interface UseUpdateCheckpointResult {
  /** Update a single checkpoint */
  updateCheckpoint: (data: CheckpointUpdateData) => Promise<void>;
  /** Whether the mutation is in progress */
  isUpdating: boolean;
  /** Error from the last mutation attempt */
  error: Error | null;
  /** Reset the error state */
  reset: () => void;
}

/**
 * Hook to update checkpoint data
 *
 * @param options - Configuration options
 * @returns Mutation function and state
 */
export function useUpdateCheckpoint(
  options: UseUpdateCheckpointOptions
): UseUpdateCheckpointResult {
  const { scheduleAssignmentId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CheckpointUpdateData) => {
      // Convert to the format expected by updateCheckpoints API
      const payload = [{
        CheckpointID: data.checkpointId,
        Comment: data.comment ?? '',
        Time: data.time ?? '',
        DateTime: data.dateTime ?? null,
        SelectedStatusID: data.selectedStatusId ?? null,
        SelectedDropdownIDs: data.selectedDropdownIds ?? [],
      }] as any;

      await updateCheckpoints(payload);
      return data; // Return data for use in onSuccess
    },

    // Optimistic update - immediately update the cache before API completes
    onMutate: async (data: CheckpointUpdateData) => {
      if (!scheduleAssignmentId) return;

      const queryKey = [...PATROL_DETAIL_QUERY_KEY, scheduleAssignmentId];

      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<PatrolDetailsResponse>(queryKey);

      // Optimistically update the cache
      if (previousData?.Checklists) {
        queryClient.setQueryData<PatrolDetailsResponse>(queryKey, (old) => {
          if (!old?.Checklists) return old;

          return {
            ...old,
            Checklists: old.Checklists.map((checklist) => ({
              ...checklist,
              Checkpoints: (checklist.Checkpoints || []).map((checkpoint) => {
                if (checkpoint.CheckpointID !== data.checkpointId) {
                  return checkpoint;
                }

                // Apply the optimistic update
                return {
                  ...checkpoint,
                  Comment: data.comment ?? checkpoint.Comment,
                  Time: data.time ?? checkpoint.Time,
                  DateTime: data.dateTime !== undefined ? data.dateTime : checkpoint.DateTime,
                  SelectedStatusID: data.selectedStatusId !== undefined
                    ? data.selectedStatusId
                    : checkpoint.SelectedStatusID,
                  SelectedDropdownIDs: data.selectedDropdownIds ?? checkpoint.SelectedDropdownIDs,
                };
              }),
            })),
          };
        });
      }

      // Return context with previous data for rollback
      return { previousData, queryKey };
    },

    // On error, rollback to the previous value
    onError: (error: Error, _data, context) => {
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      onError?.(error);
    },

    // On success, invalidate to ensure we're in sync with server
    onSuccess: () => {
      // Invalidate to refetch fresh data (ensures server state is reflected)
      if (scheduleAssignmentId) {
        queryClient.invalidateQueries({
          queryKey: [...PATROL_DETAIL_QUERY_KEY, scheduleAssignmentId],
        });
      }
      // Also invalidate activity feed as checkpoint changes may affect it
      queryClient.invalidateQueries({ queryKey: ACTIVITY_FEED_QUERY_KEY });

      onSuccess?.();
    },
  });

  const updateCheckpoint = useCallback(
    async (data: CheckpointUpdateData) => {
      await mutation.mutateAsync(data);
    },
    [mutation]
  );

  return {
    updateCheckpoint,
    isUpdating: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    reset: mutation.reset,
  };
}

export default useUpdateCheckpoint;
